// ╔══════════════════════════════════════════════════════════════╗
// ║  Reports page — 3 tabs (exits + salaries + production)        ║
// ║  Each: date range filter + table + Excel export               ║
// ╚══════════════════════════════════════════════════════════════╝

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useToast } from '../components/Toast.jsx';
import { fmtAR, fmtMoney, fmtDate } from '../lib/format.js';
import { Badge, Btn, Card } from '../components/ui.jsx';
import DateRangePicker from '../components/DateRangePicker.jsx';
import Icon from '../components/Icon.jsx';

const REPORTS = [
  { id: 'exits', l: 'تقرير الخروج', i: 'x' },
  { id: 'salaries', l: 'تقرير المرتبات', i: 'wallet' },
  { id: 'production', l: 'تقرير الإنتاج', i: 'factory' },
];

const EXIT_REASON_AR = {
  resignation: 'استقالة',
  termination: 'إنهاء خدمة',
  retirement: 'تقاعد',
  death: 'وفاة',
  absconded: 'ترك العمل',
  other: 'آخر',
};

export default function ReportsPage() {
  const [tab, setTab] = useState('exits');
  const [range, setRange] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
    to: new Date().toISOString().slice(0, 10),
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-up">
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
          التقارير
        </div>
        <h1 className="text-2xl md:text-3xl font-black mt-1">تقارير الإدارة</h1>
        <p className="text-sm text-ink-400 mt-1">
          استخرج تقارير الخروج والرواتب والإنتاج لأى فترة زمنية مع إمكانية تصديرها لـ Excel
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-ink-100 dark:border-ink-800 overflow-x-auto no-scrollbar">
        {REPORTS.map((r) => (
          <button
            key={r.id}
            onClick={() => setTab(r.id)}
            className={`px-4 h-12 text-sm font-semibold border-b-2 -mb-px transition whitespace-nowrap inline-flex items-center gap-2 ${
              tab === r.id
                ? 'border-brand-500 text-brand-600 dark:text-brand-300'
                : 'border-transparent text-ink-400 hover:text-ink-700 dark:hover:text-ink-100'
            }`}
          >
            <Icon name={r.i} size={14} />
            {r.l}
          </button>
        ))}
      </div>

      {/* Range filter */}
      <Card className="p-4">
        <DateRangePicker value={range} onChange={setRange} />
      </Card>

      {tab === 'exits' && <ExitsReport range={range} />}
      {tab === 'salaries' && <SalariesReport range={range} />}
      {tab === 'production' && <ProductionReport range={range} />}
    </div>
  );
}

// ─── Exits report ───
function ExitsReport({ range }) {
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['exits', range],
    queryFn: () =>
      api.get('/exits', { params: { from: range.from, to: range.to } }).then((r) => r.data),
    enabled: !!range.from,
  });

  const { data: stats } = useQuery({
    queryKey: ['exits', 'stats', range],
    queryFn: () =>
      api
        .get('/exits/stats', { params: { from: range.from, to: range.to } })
        .then((r) => r.data),
    enabled: !!range.from,
  });

  const exits = data?.exits ?? [];

  function exportToCSV() {
    if (exits.length === 0) {
      toast.push({ tone: 'warn', title: 'لا توجد بيانات' });
      return;
    }
    const headers = ['رقم الموظف', 'الاسم', 'القسم', 'المسمى', 'السبب', 'تاريخ الخروج', 'المخالصة', 'الملاحظات'];
    const lines = [headers.join(',')];
    for (const e of exits) {
      lines.push([
        e.employeeId,
        `"${(e.employeeName ?? '').replace(/"/g, '""')}"`,
        e.deptId ?? '',
        `"${(e.position ?? '').replace(/"/g, '""')}"`,
        EXIT_REASON_AR[e.reason] ?? e.reason,
        e.exitDate,
        e.finalSettlement ?? '',
        `"${(e.interviewNotes ?? '').replace(/"/g, '""')}"`,
      ].join(','));
    }
    download(lines.join('\n'), `exits_${range.from}_to_${range.to}.csv`);
  }

  return (
    <div className="space-y-4">
      {stats && stats.total > 0 && (
        <Card className="p-5">
          <h3 className="font-bold mb-3">ملخّص الخروج ({fmtAR(stats.total)} حالة)</h3>
          <div className="flex flex-wrap gap-2">
            {stats.byReason.map((r) => (
              <Badge key={r.reason} tone="neutral">
                {EXIT_REASON_AR[r.reason]}: {fmtAR(r.count)}
              </Badge>
            ))}
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between p-4">
          <h3 className="font-bold">سجلات الخروج ({fmtAR(exits.length)})</h3>
          <Btn variant="outline" size="sm" icon="download" onClick={exportToCSV}>
            تصدير Excel
          </Btn>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 dark:bg-ink-800/40 text-xs text-ink-500">
              <tr>
                {['#', 'الموظف', 'القسم', 'السبب', 'التاريخ', 'المخالصة', 'موثَّق بواسطة'].map((h) => (
                  <th key={h} className="text-start font-semibold px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-soft">
              {isLoading && (
                <tr><td colSpan="7" className="p-8 text-center text-ink-400">جاري التحميل…</td></tr>
              )}
              {!isLoading && exits.length === 0 && (
                <tr><td colSpan="7" className="p-8 text-center text-ink-400">لا توجد حالات خروج في هذه الفترة</td></tr>
              )}
              {exits.map((e) => (
                <tr key={e.id} className="hover:bg-ink-50 dark:hover:bg-ink-800/40">
                  <td className="px-4 py-3 font-mono text-[11px] text-ink-400">{e.employeeId}</td>
                  <td className="px-4 py-3 font-semibold">{e.employeeName}</td>
                  <td className="px-4 py-3">{e.deptId}</td>
                  <td className="px-4 py-3"><Badge tone="warn">{EXIT_REASON_AR[e.reason]}</Badge></td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtDate(e.exitDate)}</td>
                  <td className="px-4 py-3 tabular">{e.finalSettlement ? fmtMoney(e.finalSettlement) : '—'}</td>
                  <td className="px-4 py-3 text-xs">{e.documentedByName ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── Salaries report ───
function SalariesReport({ range }) {
  const toast = useToast();
  const { data: runsData } = useQuery({
    queryKey: ['payroll', 'runs'],
    queryFn: () => api.get('/payroll/runs').then((r) => r.data),
  });

  // Show runs within the date range
  const runs = (runsData?.runs ?? []).filter(
    (r) => r.month >= range.from && r.month <= range.to,
  );

  function exportRun(runId, month) {
    window.location.href = `/api/payroll/runs/${runId}/export`;
    // Note: this uses the existing download endpoint
  }

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between p-4">
        <h3 className="font-bold">دورات الرواتب فى الفترة ({fmtAR(runs.length)})</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 dark:bg-ink-800/40 text-xs text-ink-500">
            <tr>
              {['الشهر', 'الحالة', 'الموظفين', 'الإجمالى', 'الصافي', ''].map((h, i) => (
                <th key={i} className="text-start font-semibold px-4 py-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-soft">
            {runs.length === 0 && (
              <tr><td colSpan="6" className="p-8 text-center text-ink-400">لا توجد دورات في هذه الفترة</td></tr>
            )}
            {runs.map((r) => (
              <tr key={r.id} className="hover:bg-ink-50 dark:hover:bg-ink-800/40">
                <td className="px-4 py-3 font-bold">
                  {new Date(r.month).toLocaleDateString('ar-EG', { month: 'long', year: 'numeric' })}
                </td>
                <td className="px-4 py-3">
                  <Badge tone={r.status === 'approved' ? 'success' : 'warn'} dot>
                    {r.status === 'approved' ? 'معتمد' : 'محسوب'}
                  </Badge>
                </td>
                <td className="px-4 py-3 tabular">{fmtAR(r.employeeCount)}</td>
                <td className="px-4 py-3 tabular">{fmtMoney(r.totalGross)}</td>
                <td className="px-4 py-3 tabular font-extrabold">{fmtMoney(r.totalNet)}</td>
                <td className="px-4 py-3">
                  <Btn size="sm" variant="outline" icon="download" onClick={() => exportRun(r.id, r.month)}>
                    Excel
                  </Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─── Production report ───
function ProductionReport({ range }) {
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['production', 'entries', { from: range.from, to: range.to }],
    queryFn: () =>
      api
        .get('/production/entries', {
          params: { from: range.from, to: range.to, pageSize: 1000 },
        })
        .then((r) => r.data),
    enabled: !!range.from,
  });

  const entries = data?.entries ?? [];

  // Aggregate
  const summary = entries.reduce(
    (a, e) => ({
      total: a.total + e.actual,
      target: a.target + e.target,
      hits: a.hits + (Number(e.delta) > 0 ? 1 : 0),
      misses: a.misses + (Number(e.delta) < 0 ? 1 : 0),
      bonusTotal: a.bonusTotal + Math.max(0, Number(e.delta)),
      penaltyTotal: a.penaltyTotal + Math.max(0, -Number(e.delta)),
    }),
    { total: 0, target: 0, hits: 0, misses: 0, bonusTotal: 0, penaltyTotal: 0 },
  );

  function exportToCSV() {
    if (entries.length === 0) return;
    const headers = ['التاريخ', 'العامل', 'المرحلة', 'الهدف', 'الفعلى', 'الفرق', 'الحالة'];
    const lines = [headers.join(',')];
    for (const e of entries) {
      lines.push([
        e.date,
        `"${(e.workerName ?? '').replace(/"/g, '""')}"`,
        e.stageId,
        e.target,
        e.actual,
        e.delta,
        e.status,
      ].join(','));
    }
    download(lines.join('\n'), `production_${range.from}_to_${range.to}.csv`);
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="إجمالى الإنتاج" value={fmtAR(summary.total)} sub="قطعة" tone="brand" />
        <SummaryCard label="الهدف" value={fmtAR(summary.target)} sub={`${summary.target > 0 ? Math.round((summary.total / summary.target) * 100) : 0}%`} />
        <SummaryCard label="أيام تحقّق الهدف" value={fmtAR(summary.hits)} tone="success" />
        <SummaryCard label="إجمالى المكافآت" value={fmtMoney(summary.bonusTotal)} tone="success" />
      </div>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between p-4">
          <h3 className="font-bold">إدخالات الإنتاج ({fmtAR(entries.length)})</h3>
          <Btn variant="outline" size="sm" icon="download" onClick={exportToCSV}>
            تصدير Excel
          </Btn>
        </div>
        <div className="overflow-x-auto max-h-[60vh]">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 dark:bg-ink-800/40 text-xs text-ink-500 sticky top-0">
              <tr>
                {['التاريخ', 'العامل', 'المرحلة', 'الهدف', 'الفعلى', 'الأثر', 'الحالة'].map((h, i) => (
                  <th key={i} className="text-start font-semibold px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-soft">
              {isLoading && (
                <tr><td colSpan="7" className="p-8 text-center text-ink-400">جاري التحميل…</td></tr>
              )}
              {!isLoading && entries.length === 0 && (
                <tr><td colSpan="7" className="p-8 text-center text-ink-400">لا توجد إدخالات</td></tr>
              )}
              {entries.map((e) => (
                <tr key={e.id} className="hover:bg-ink-50 dark:hover:bg-ink-800/40">
                  <td className="px-4 py-3 font-mono text-xs">{e.date}</td>
                  <td className="px-4 py-3 font-semibold">{e.workerName}</td>
                  <td className="px-4 py-3">{e.stageId}</td>
                  <td className="px-4 py-3 tabular">{fmtAR(e.target)}</td>
                  <td className="px-4 py-3 tabular font-bold">{fmtAR(e.actual)}</td>
                  <td className="px-4 py-3">
                    {Number(e.delta) > 0 ? (
                      <Badge tone="success">+{fmtAR(e.delta)}</Badge>
                    ) : Number(e.delta) < 0 ? (
                      <Badge tone="danger">{fmtAR(e.delta)}</Badge>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={e.status === 'approved' ? 'success' : e.status === 'rejected' ? 'danger' : 'warn'} dot>
                      {e.status === 'approved' ? 'معتمد' : e.status === 'rejected' ? 'مرفوض' : 'تحت المراجعة'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function SummaryCard({ label, value, sub, tone = 'neutral' }) {
  const tones = {
    neutral: 'text-ink-700 dark:text-ink-100',
    brand: 'text-brand-600 dark:text-brand-300',
    success: 'text-emerald-600',
    warn: 'text-amber-600',
  };
  return (
    <div className="p-4 rounded-2xl bg-ink-50 dark:bg-ink-800/40">
      <div className="text-[11px] uppercase text-ink-400 font-bold">{label}</div>
      <div className={`text-2xl font-black tabular mt-0.5 ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[11px] text-ink-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function download(text, filename) {
  const blob = new Blob(['﻿' + text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
